/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
//import { end } from "@popperjs/core";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig = {
    auth: {
        clientId: '5fb55a5e-39ac-48a6-8c92-140ddde5136e', // This is the ONLY mandatory field that you need to supply.
        authority: 'https://login.microsoftonline.com/2f4006c5-c4ea-4165-846f-914b5b75685b', // Replace the placeholder with your tenant subdomain
        redirectUri: 'https://slavkopar.github.io/knowledge', // You must register this URI on Microsoft Entra admin center/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            /**
             * Below you can configure MSAL.js logs. For more information, visit:
             * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
             */
            loggerCallback: (level: unknown, message: unknown, containsPii: unknown) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */

export const protectedResources = (() => {
    const URL = (import.meta.env.VITE_KNOWLEDGE_API_URL ?? 'UNK') as string;

    return {
        KnowledgeAPI: {
            endPoints: {
                Category: {
                    Row: `${URL}/CategoryRow`,
                    Category: `${URL}/Category`,
                    Question: `${URL}/Question`,
                    QuestionAnswer: `${URL}/QuestionAnswer`,
                    //endpointShortGroup: `${VITE_KNOWLEDGE_LIB_API_URL}/ShortGroup`,
                },
                Group: {
                    Row: `${URL}/GroupRow`,
                    Group: `${URL}/Group`,
                    Answer: `${URL}/Answer`
                    //endpointShortGroup: `${VITE_KNOWLEDGE_LIB_API_URL}/ShortGroup`,
                },
                History: {
                    endpointHistory: `${URL}/History`,
                    endpointHistoryFilter: `${URL}/HistoryFilter`
                },
                Workspace: {
                    endpointWorkspace: `${URL}/Workspace`
                }
            }
        },
        
        scopes: {
            read: ['api://a27f5cf6-8359-411b-a430-c4bd8aa5e6af/ToDoList.Read'],
            write: ['api://a27f5cf6-8359-411b-a430-c4bd8aa5e6af/ToDoList.ReadWrite']
        }
    };

})();

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.scopes.read, ...protectedResources.scopes.write]
};
